// Agency SOPs. One SOP per agency (trade) shared by every project, plus a Common SOP every agency
// follows. A project can add its own points or turn a global point off (with a reason).
// English is the master text and drives version numbers; Gujarati is a checked translation per point.

export const SOP_SECTIONS = ['Before start', 'During work', 'Quality checks', 'Billing & handover'] as const;
export const SOP_SECTIONS_GU = ['કામ શરૂ કરતા પહેલા', 'કામ દરમિયાન', 'ક્વોલિટી ચેક', 'બિલિંગ અને હેન્ડઓવર'] as const;
export const SOP_LABELS_GU = { hold: 'હોલ્ડ પોઇન્ટ', projectOnly: 'ફક્ત આ પ્રોજેક્ટ', common: 'બધી એજન્સી માટે સામાન્ય' };
export const COMMON_SOP_ID = 'common';

/** Site Gujarati word list. The translate function uses the same list. */
export const SOP_GLOSSARY: Array<[string, string]> = [
  ['Hold point', 'હોલ્ડ પોઇન્ટ'], ['Site engineer', 'સાઇટ એન્જિનિયર'], ['Pressure test', 'પ્રેશર ટેસ્ટ'], ['Work order', 'વર્ક ઓર્ડર'],
  ['Change order', 'ચેન્જ ઓર્ડર'], ['Running bill', 'રનિંગ બિલ'], ['Retention', 'રીટેન્શન'], ['Handover', 'હેન્ડઓવર'],
  ['Slab', 'સ્લેબ'], ['Plaster', 'પ્લાસ્ટર'], ['Tiles', 'ટાઇલ્સ'], ['Curing', 'ક્યોરિંગ'], ['Leakage', 'લીકેજ'], ['Layout drawing', 'લેઆઉટ ડ્રોઇંગ'],
];
export const SOP_KEEP_AS_IS = 'CPVC, UPVC, BOCW, ISI, BWP, IS 1200, BBS, megger, brand names, numbers and units';

export const SOP_IMPACTS: Array<{ value: SopImpact; label: string }> = [
  { value: 'rework', label: 'Rework' }, { value: 'delay', label: 'Delay' }, { value: 'cost', label: 'Extra cost' },
  { value: 'quality', label: 'Quality' }, { value: 'safety', label: 'Safety' }, { value: 'good', label: 'Worked well' },
];

export type GuState = 'ok' | 'ai' | 'stale';
export type SopImpact = 'rework' | 'delay' | 'cost' | 'quality' | 'safety' | 'good';
export type SopPoint = { id: string; section: number; text: string; hold: boolean; gu?: { text: string; state: GuState } };
export type SopProjectPoint = SopPoint & { after?: string };
export type SopLogEntry = { v: number; note: string; gu?: boolean; at?: string; by?: string };
/** The next version being prepared. The approved `clauses` stay untouched until it is published. */
export type SopDraft = { clauses: SopPoint[]; basedOn: number; notes: string[] };
/** SOP writing patterns; same list as POINT_TYPES in functions/src/sopTranslate.ts. */
export const SOP_POINT_TYPES = {
  method: 'Method', included: 'Included in scope', excluded: 'Not in scope', hold: 'Hold point',
  liability: 'Damage / liability', material: 'Material', test: 'Test / check', never: 'Never / safety',
} as const;
export type SopPointType = keyof typeof SOP_POINT_TYPES;
export type SopProposalKind ='new' | 'reword' | 'duplicate' | 'vendor';
/** One AI suggestion made from pending points, reviewed by a person before it touches the draft. */
export type SopProposal = {
  id: string; kind: SopProposalKind; sourceIds: string[]; targetId?: string;
  en: string; gu: string; section: number; hold: boolean; reason: string;
  status: 'open' | 'accepted' | 'rejected';
  /** Header placeholders the AI put in the point; swapped for plain words on the server. */
  removedPlaceholders?: string[];
  /** Which SOP writing pattern the AI used. */
  pointType?: SopPointType;
  /** Set when the proposal comes from an old SOP document instead of pending points. */
  source?: { kind: 'oldSop'; label: string; text: string };
  /** The old document disagrees with the current point; the reviewer decides. */
  conflict?: boolean;
};
export type SopReview = { proposals: SopProposal[]; at: string; by: string };
export type SopAgency = {
  id: string; name: string; nameGu?: string; order: number; archived?: boolean;
  /** 'all', or the project ids that use this agency. */
  projects: 'all' | string[];
  /** The approved SOP: what agencies receive and what projects follow. */
  version: number; clauses: SopPoint[]; log: SopLogEntry[];
  draft?: SopDraft | null;
  review?: SopReview | null;
  /** Sample values per placeholder key. On the Common SOP these are the defaults for every agency. */
  samples?: Record<string, SopSample>;
  /** What must be decided or done before this agency starts on a project. Answers are kept per project. */
  checklist?: SopCheckItem[];
  /** A site team list (e.g. Project set-up): start checklist only, no SOP points or PDF, not offered as an agency. */
  siteTeam?: boolean;
  /** Heading the SOP sits under in the list (a key of SOP_GROUPS). */
  group?: string;
};

/** Headings for the SOP list, in display order. SOPs without a known group show under "Other". */
export const SOP_GROUPS: Array<{ key: string; label: string }> = [
  { key: 'setup', label: 'Project set-up' },
  { key: 'structure', label: 'Structure' },
  { key: 'plumbing', label: 'Plumbing' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'flooring', label: 'Flooring & stone' },
  { key: 'finishes', label: 'Wood, aluminium & finishes' },
  { key: 'services', label: 'Services' },
];

/** Agencies under their headings, keeping each agency's own order inside a group. */
export function groupAgencies<T extends Pick<SopAgency, 'group'>>(agencies: T[]): Array<{ key: string; label: string; items: T[] }> {
  const known = new Set(SOP_GROUPS.map((g) => g.key));
  const out = SOP_GROUPS.map((g) => ({ ...g, items: agencies.filter((a) => a.group === g.key) }));
  out.push({ key: 'other', label: 'Other', items: agencies.filter((a) => !a.group || !known.has(a.group)) });
  return out.filter((g) => g.items.length);
}

/** A decision needs an answer (brand, grade, model) and prints in the PDF; a task is ticked by the site team. */
export type SopCheckKind = 'decision' | 'task';
export type SopCheckItem = { id: string; kind: SopCheckKind; en: string; gu?: string; hint?: string };
export type SopCheckAnswer = { value?: string; done?: boolean; by?: string; at?: string };

export function checklistStatus(items: SopCheckItem[] | undefined, answers: Record<string, SopCheckAnswer> | undefined) {
  const list = items || [];
  const isReady = (it: SopCheckItem) => (it.kind === 'decision' ? !!String(answers?.[it.id]?.value || '').trim() : !!answers?.[it.id]?.done);
  const open = list.filter((it) => !isReady(it));
  return {
    total: list.length,
    ready: list.length - open.length,
    openDecisions: open.filter((it) => it.kind === 'decision'),
    openTasks: open.filter((it) => it.kind === 'task'),
    isReady,
  };
}

/** Answered decisions of the agency and Common, for the PDF's "Approved materials" table. */
export function approvedMaterials(
  sources: Array<{ items: SopCheckItem[] | undefined; answers: Record<string, SopCheckAnswer> | undefined }>,
): Array<{ label: string; labelGu: string; value: string }> {
  return sources.flatMap(({ items, answers }) => (items || [])
    .filter((it) => it.kind === 'decision' && String(answers?.[it.id]?.value || '').trim())
    .map((it) => ({ label: it.en, labelGu: it.gu || '', value: String(answers![it.id].value).trim() })));
}
export type SopOverride = { added: SopProjectPoint[]; off: Record<string, string> };
/** 'new' is a pending point waiting for review. */
export type SopLessonStatus = 'new' | 'added' | 'duplicate' | 'vendor' | 'rejected';
export type SopLesson = {
  id: string; agencyId: string; projectId: string; vendor?: string; impact: SopImpact; what: string; suggest?: string;
  status: SopLessonStatus; result?: string; en?: string; date: string; createdByName?: string;
};
export type SopRow = SopPoint & { from: 'global' | 'project'; after?: string; off: boolean; reason: string; num: string };

export const EMPTY_OVERRIDE: SopOverride = { added: [], off: {} };
export const LOG_LIMIT = 100;

export function newPointId(): string {
  return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function guState(point: Pick<SopPoint, 'gu'>): GuState | 'missing' {
  return point.gu && point.gu.text ? point.gu.state : 'missing';
}

export const hasGujaratiScript = (text: string | undefined) => /[઀-૿]/.test(text || '');

export function usedOnProject(agency: Pick<SopAgency, 'id' | 'projects'>, projectId: string | null | undefined): boolean {
  if (agency.id === COMMON_SOP_ID || agency.projects === 'all') return true;
  return !!projectId && Array.isArray(agency.projects) && agency.projects.includes(projectId);
}

/** Agency names match when they are equal after removing case, spaces and punctuation. */
export function agencyKey(name: string): string {
  return String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** One section of one SOP, in order. Project points sit after the point they follow, else at the end.
 * Pass no override for the global view. */
export function sectionRows(agency: Pick<SopAgency, 'id' | 'clauses'>, override: SopOverride | null, section: number): SopRow[] {
  const o = override || EMPTY_OVERRIDE;
  const rows: Array<SopPoint & { from: 'global' | 'project'; after?: string }> = agency.clauses
    .filter((x) => x.section === section).map((x) => ({ ...x, from: 'global' as const }));
  o.added.filter((x) => x.section === section).forEach((x) => {
    const item = { ...x, from: 'project' as const };
    let i = x.after ? rows.findIndex((r) => r.id === x.after) : -1;
    if (i >= 0) {
      while (rows[i + 1] && rows[i + 1].from === 'project' && rows[i + 1].after === x.after) i++;
      rows.splice(i + 1, 0, item);
    } else rows.push(item);
  });
  let n = 0;
  const prefix = agency.id === COMMON_SOP_ID ? 'C' : '';
  return rows.map((r) => {
    const off = !!override && o.off[r.id] !== undefined;
    return { ...r, off, reason: off ? o.off[r.id] : '', num: off ? '' : `${prefix}${section + 1}.${++n}` };
  });
}

export function allRows(agency: Pick<SopAgency, 'id' | 'clauses'>, override: SopOverride | null): SopRow[] {
  return SOP_SECTIONS.flatMap((_, si) => sectionRows(agency, override, si));
}

export function insertAfter<T extends { id: string }>(list: T[], item: T, afterId?: string): T[] {
  const next = [...list];
  const i = afterId ? next.findIndex((x) => x.id === afterId) : -1;
  if (i >= 0) next.splice(i + 1, 0, item); else next.push(item);
  return next;
}

export function pushLog(log: SopLogEntry[], entry: SopLogEntry): SopLogEntry[] {
  return [...log, entry].slice(-LOG_LIMIT);
}

export function shortText(text: string, n = 56): string {
  return text.length > n ? text.slice(0, n - 1) + '…' : text;
}

/** Gujarati readiness for giving an SOP to an agency: its live points plus the live common points. */
export function guReadiness(rows: SopRow[]): { ok: number; ai: number; stale: number; missing: number; total: number; ready: boolean } {
  const live = rows.filter((r) => !r.off);
  const counts = { ok: 0, ai: 0, stale: 0, missing: 0 };
  live.forEach((r) => { counts[guState(r)]++; });
  return { ...counts, total: live.length, ready: live.length > 0 && counts.ok === live.length };
}

/** After an English edit, a checked or drafted Gujarati becomes stale. */
export function applyEnglishEdit(point: SopPoint, text: string, hold: boolean): SopPoint {
  const changed = text !== point.text;
  const next: SopPoint = { ...point, text, hold };
  if (changed && point.gu && point.gu.text) next.gu = { ...point.gu, state: 'stale' };
  return next;
}

// ---------- Draft, review and publish ----------

/** The points being worked on: the draft when there is one, else the approved SOP. */
export function workingClauses(agency: Pick<SopAgency, 'clauses' | 'draft'>): SopPoint[] {
  return agency.draft ? agency.draft.clauses : agency.clauses;
}

export function startDraft(agency: Pick<SopAgency, 'clauses' | 'version' | 'draft'>): SopDraft {
  return agency.draft || { clauses: agency.clauses.map((p) => ({ ...p })), basedOn: agency.version, notes: [] };
}

export type DraftMark = 'new' | 'changed' | null;
/** How a draft point differs from the approved one with the same id. */
export function draftMark(point: SopPoint, approved: SopPoint[]): DraftMark {
  const before = approved.find((p) => p.id === point.id);
  if (!before) return 'new';
  const same = before.text === point.text && before.hold === point.hold && before.section === point.section &&
    (before.gu?.text || '') === (point.gu?.text || '') && (before.gu?.state || '') === (point.gu?.state || '');
  return same ? null : 'changed';
}

export function draftSummary(agency: Pick<SopAgency, 'clauses' | 'draft'>): { added: number; changed: number; removed: number; total: number } {
  if (!agency.draft) return { added: 0, changed: 0, removed: 0, total: 0 };
  let added = 0;
  let changed = 0;
  agency.draft.clauses.forEach((p) => {
    const m = draftMark(p, agency.clauses);
    if (m === 'new') added++; else if (m === 'changed') changed++;
  });
  const removed = agency.clauses.filter((p) => !agency.draft!.clauses.some((d) => d.id === p.id)).length;
  return { added, changed, removed, total: added + changed + removed };
}

/** Points that stop a publish: every point must have checked Gujarati, since every PDF is bilingual. */
export function publishBlockers(clauses: SopPoint[]): SopPoint[] {
  return clauses.filter((p) => guState(p) !== 'ok');
}

/** Apply one accepted AI proposal to the draft points. Duplicate and vendor proposals change no points. */
export function applyProposal(clauses: SopPoint[], proposal: SopProposal, guChecked: boolean): SopPoint[] {
  const gu = proposal.gu.trim() ? { text: proposal.gu.trim(), state: (guChecked ? 'ok' : 'ai') as GuState } : undefined;
  if (proposal.kind === 'reword' && proposal.targetId && clauses.some((p) => p.id === proposal.targetId)) {
    return clauses.map((p) => {
      if (p.id !== proposal.targetId) return p;
      const next: SopPoint = { ...p, text: proposal.en.trim(), hold: proposal.hold, section: proposal.section };
      if (gu) next.gu = gu; else delete next.gu;
      return next;
    });
  }
  if (proposal.kind === 'new' || proposal.kind === 'reword') {
    const point: SopPoint = { id: newPointId(), section: proposal.section, text: proposal.en.trim(), hold: proposal.hold };
    if (gu) point.gu = gu;
    return [...clauses, point];
  }
  return clauses;
}

// ---------- Placeholders: details that change with each project, filled in when the PDF is made ----------

export type SopPlaceholderKind = 'text' | 'longtext' | 'date' | 'number';
export type SopSample = { en?: string; gu?: string };
export type SopPlaceholder = {
  key: string; label: string; labelGu: string; group: string; kind: SopPlaceholderKind;
  /** Built-in sample, used when neither Common nor the agency sets one. */
  sample: { en: string; gu: string };
  translate?: boolean; always?: boolean;
  /** Prints in the PDF details table only, never inside a point. */
  header?: boolean;
};

/** The fixed list. Points use them as {{key}}; the PDF pop-up asks for the ones in use.
 * Keep in sync with TERM_PLACEHOLDERS / HEADER_PLACEHOLDERS in functions/src/sopTranslate.ts. */
export const SOP_PLACEHOLDERS: SopPlaceholder[] = [
  { key: 'agency_firm', label: 'Agency firm name', labelGu: 'એજન્સીનું નામ', group: 'Agency', kind: 'text', always: true, header: true, sample: { en: 'Shree Ganesh Plumbing', gu: 'શ્રી ગણેશ પ્લમ્બિંગ' } },
  { key: 'contact_person', label: 'Contact person', labelGu: 'સંપર્ક વ્યક્તિ', group: 'Agency', kind: 'text', always: true, header: true, sample: { en: 'Rameshbhai Patel', gu: 'રમેશભાઈ પટેલ' } },
  { key: 'contact_phone', label: 'Contact phone', labelGu: 'ફોન નંબર', group: 'Agency', kind: 'text', always: true, header: true, sample: { en: '98250 12345', gu: '98250 12345' } },
  { key: 'work_order_no', label: 'Work order no.', labelGu: 'વર્ક ઓર્ડર નંબર', group: 'Agency', kind: 'text', always: true, header: true, sample: { en: 'WO/AVSAR/PL/014', gu: 'WO/AVSAR/PL/014' } },
  { key: 'scope_of_work', label: 'Scope of work', labelGu: 'કામનો વિસ્તાર', group: 'Scope and time', kind: 'longtext', translate: true, always: true, header: true, sample: { en: 'Tower A and B, all 48 flats, as per drawings', gu: 'ટાવર A અને B, બધા 48 ફ્લેટ, ડ્રોઇંગ મુજબ' } },
  { key: 'start_date', label: 'Start date', labelGu: 'શરૂઆતની તારીખ', group: 'Scope and time', kind: 'date', always: true, sample: { en: '2026-11-01', gu: '2026-11-01' } },
  { key: 'completion_period', label: 'Completion period', labelGu: 'કામ પૂરું કરવાનો સમય', group: 'Scope and time', kind: 'text', translate: true, always: true, sample: { en: '6 months', gu: '6 મહિના' } },
  { key: 'delay_penalty', label: 'Delay penalty', labelGu: 'વિલંબ દંડ', group: 'Scope and time', kind: 'text', translate: true, sample: { en: '₹500 per day', gu: 'દરરોજ ₹500' } },
  { key: 'payment_terms', label: 'Payment terms', labelGu: 'પેમેન્ટની શરતો', group: 'Payment', kind: 'longtext', translate: true, sample: { en: '90% of each certified running bill; balance with the final bill after handover', gu: 'દરેક સર્ટિફાઇડ રનિંગ બિલના 90%; બાકી રકમ હેન્ડઓવર પછી ફાઇનલ બિલ સાથે' } },
  { key: 'advance', label: 'Advance', labelGu: 'એડવાન્સ', group: 'Payment', kind: 'text', translate: true, sample: { en: 'Nil', gu: 'નથી' } },
  { key: 'bill_cycle', label: 'Running bill cycle', labelGu: 'રનિંગ બિલ સમયગાળો', group: 'Payment', kind: 'text', translate: true, sample: { en: 'every 15 days', gu: 'દર 15 દિવસે' } },
  { key: 'payment_days', label: 'Days to pay after certification', labelGu: 'સર્ટિફિકેશન પછી પેમેન્ટના દિવસ', group: 'Payment', kind: 'number', sample: { en: '7', gu: '7' } },
  { key: 'retention_percent', label: 'Retention %', labelGu: 'રીટેન્શન %', group: 'Retention and warranty', kind: 'number', sample: { en: '5', gu: '5' } },
  { key: 'retention_period', label: 'Retention period', labelGu: 'રીટેન્શન સમયગાળો', group: 'Retention and warranty', kind: 'text', translate: true, sample: { en: '12 months from handover', gu: 'હેન્ડઓવરથી 12 મહિના' } },
  { key: 'warranty_period', label: 'Defect liability / warranty period', labelGu: 'વોરંટી સમયગાળો', group: 'Retention and warranty', kind: 'text', translate: true, sample: { en: '12 months', gu: '12 મહિના' } },
  { key: 'guarantee', label: 'Guarantee', labelGu: 'ગેરંટી', group: 'Retention and warranty', kind: 'longtext', translate: true, sample: { en: '5 year leakage guarantee on concealed plumbing', gu: 'કન્સિલ્ડ પ્લમ્બિંગ પર 5 વર્ષની લીકેજ ગેરંટી' } },
];
export const BLANK_VALUE = '______';
const PLACEHOLDER_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;

export const placeholderByKey = (key: string) => SOP_PLACEHOLDERS.find((p) => p.key === key);

/** Every {{key}} in the text, in order. (A plain exec loop: the app's TypeScript target cannot iterate matchAll.) */
function placeholderMatches(text: string): RegExpExecArray[] {
  const re = new RegExp(PLACEHOLDER_RE.source, 'g');
  const out: RegExpExecArray[] = [];
  let m = re.exec(text);
  while (m) { out.push(m); m = re.exec(text); }
  return out;
}

/** Placeholder keys used in these texts, in list order. Unknown keys are ignored. */
export function placeholdersUsed(texts: string[]): string[] {
  const found = new Set<string>();
  texts.forEach((t) => { placeholderMatches(String(t || '')).forEach((m) => found.add(m[1])); });
  return SOP_PLACEHOLDERS.map((p) => p.key).filter((k) => found.has(k));
}

/** Split text into plain parts and placeholder parts, for showing chips on screen. */
export function splitPlaceholders(text: string): Array<{ text: string; key?: string }> {
  const out: Array<{ text: string; key?: string }> = [];
  let last = 0;
  placeholderMatches(String(text || '')).forEach((m) => {
    if (m.index! > last) out.push({ text: text.slice(last, m.index) });
    out.push({ text: m[0], key: m[1] });
    last = m.index! + m[0].length;
  });
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

export function formatPlaceholderValue(key: string, value: string): string {
  const v = String(value ?? '').trim();
  if (!v) return '';
  if (placeholderByKey(key)?.kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return new Date(`${v}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return v;
}

/** Sample for a placeholder: the agency's own, else the Common SOP's (the global default), else built-in. */
export function sampleFor(
  key: string,
  agency: Pick<SopAgency, 'id' | 'samples'> | null,
  common: Pick<SopAgency, 'samples'> | null,
): { en: string; gu: string; from: 'agency' | 'common' | 'builtin' } {
  const base = placeholderByKey(key)?.sample || { en: '', gu: '' };
  const own = agency && agency.id !== COMMON_SOP_ID ? agency.samples?.[key] : undefined;
  const glob = agency?.id === COMMON_SOP_ID ? agency.samples?.[key] : common?.samples?.[key];
  const pick = (lang: 'en' | 'gu') => String(own?.[lang] || '').trim() || String(glob?.[lang] || '').trim() || base[lang];
  const from = own && (own.en || own.gu) ? 'agency' : glob && (glob.en || glob.gu) ? 'common' : 'builtin';
  return { en: pick('en'), gu: pick('gu'), from };
}

/** Every sample as {en, gu} value maps, for previewing points as the PDF prints them. */
export function sampleValues(agency: Pick<SopAgency, 'id' | 'samples'> | null, common: Pick<SopAgency, 'samples'> | null): { en: Record<string, string>; gu: Record<string, string> } {
  const out = { en: {} as Record<string, string>, gu: {} as Record<string, string> };
  SOP_PLACEHOLDERS.forEach((p) => { const s = sampleFor(p.key, agency, common); out.en[p.key] = s.en; out.gu[p.key] = s.gu; });
  return out;
}

export type PlaceholderIssueKind = 'gu-mismatch' | 'header-in-point' | 'long-in-sentence' | 'unknown';
export type PlaceholderIssue = { kind: PlaceholderIssueKind; row: SopRow; keys: string[] };

const keysIn = (text: string) => placeholderMatches(String(text || '')).map((m) => m[1]);

/** Where each placeholder is used, and points whose placeholders do not make sense. */
export function placeholderReport(rows: SopRow[]): { usage: Record<string, SopRow[]>; issues: PlaceholderIssue[] } {
  const usage: Record<string, SopRow[]> = {};
  const issues: PlaceholderIssue[] = [];
  rows.forEach((row) => {
    const en = keysIn(row.text);
    const gu = row.gu?.text ? keysIn(row.gu.text) : null;
    const all = Array.from(new Set([...en, ...(gu || [])]));
    all.forEach((k) => { if (placeholderByKey(k)) (usage[k] = usage[k] || []).push(row); });
    const unknown = all.filter((k) => !placeholderByKey(k));
    if (unknown.length) issues.push({ kind: 'unknown', row, keys: unknown });
    const header = all.filter((k) => placeholderByKey(k)?.header);
    if (header.length) issues.push({ kind: 'header-in-point', row, keys: header });
    const long = all.filter((k) => placeholderByKey(k)?.kind === 'longtext' && !placeholderByKey(k)?.header);
    if (long.length) issues.push({ kind: 'long-in-sentence', row, keys: long });
    if (gu) {
      const enSet = new Set(en);
      const guSet = new Set(gu);
      const diff = Array.from(new Set([...en.filter((k) => !guSet.has(k)), ...gu.filter((k) => !enSet.has(k))]));
      if (diff.length) issues.push({ kind: 'gu-mismatch', row, keys: diff });
    }
  });
  return { usage, issues };
}

/** Fill {{key}} with its value; an empty value prints a blank line to fill in by hand. */
export function fillPlaceholders(text: string, values: Record<string, string>, fallback: Record<string, string> = {}): string {
  return String(text || '').replace(PLACEHOLDER_RE, (_, key: string) => formatPlaceholderValue(key, values[key] || fallback[key] || '') || BLANK_VALUE);
}

// ---------- Bilingual PDF ----------

const escHtml = (s: string) => String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));

export type SopRateRow = { item: string; itemGu?: string; unit: string; rate: string };
export type SopPdfInput = {
  agency: Pick<SopAgency, 'id' | 'name' | 'nameGu' | 'version' | 'clauses'>;
  common: Pick<SopAgency, 'id' | 'clauses' | 'version'> | null;
  projectName: string;
  /** English values; Gujarati values fall back to the English one. */
  values: { en: Record<string, string>; gu: Record<string, string> };
  rates: SopRateRow[];
  /** Decisions from the project's start checklist. */
  approved?: Array<{ label: string; labelGu: string; value: string }>;
  issuedOn: string;
  issuedBy: string;
};

/** Every approved point that goes into the PDF, common points first. */
export function pdfRows(input: Pick<SopPdfInput, 'agency' | 'common'>): Array<{ group: 'common' | 'agency'; section: number; row: SopRow }> {
  const out: Array<{ group: 'common' | 'agency'; section: number; row: SopRow }> = [];
  if (input.common && input.agency.id !== COMMON_SOP_ID) {
    SOP_SECTIONS.forEach((_, si) => sectionRows(input.common!, null, si).forEach((row) => out.push({ group: 'common', section: si, row })));
  }
  SOP_SECTIONS.forEach((_, si) => sectionRows(input.agency, null, si).forEach((row) => out.push({ group: 'agency', section: si, row })));
  return out;
}

/** Placeholders the PDF pop-up must ask for: the always-asked details plus every one used in the points. */
export function pdfPlaceholderKeys(input: Pick<SopPdfInput, 'agency' | 'common'>): string[] {
  const used = placeholdersUsed(pdfRows(input).flatMap(({ row }) => [row.text, row.gu?.text || '']));
  return SOP_PLACEHOLDERS.filter((p) => p.always || used.includes(p.key)).map((p) => p.key);
}

/** The SOP body that goes inside the project's document template (letterhead, logos and page numbers come from it).
 * English on each line with the Gujarati directly below, key terms, rate annexure and an acknowledgement page. */
export function buildSopPdfBody(input: SopPdfInput): string {
  const rows = pdfRows(input);
  const isCommon = input.agency.id === COMMON_SOP_ID;
  const nameEn = isCommon ? 'Common for all agencies' : input.agency.name;
  const nameGu = isCommon ? SOP_LABELS_GU.common : input.agency.nameGu || '';
  const en = (text: string) => escHtml(fillPlaceholders(text, input.values.en));
  const gu = (text: string) => escHtml(fillPlaceholders(text, input.values.gu, input.values.en));
  const value = (key: string, lang: 'en' | 'gu') => escHtml(formatPlaceholderValue(key, lang === 'gu' ? input.values.gu[key] || input.values.en[key] || '' : input.values.en[key] || '') || BLANK_VALUE);
  const keys = pdfPlaceholderKeys(input);

  const block = (group: 'common' | 'agency', titleEn: string, titleGu: string) => {
    const mine = rows.filter((r) => r.group === group);
    if (!mine.length) return '';
    return `<div class="sop-group"><h2>${escHtml(titleEn)} <span class="gu">${escHtml(titleGu)}</span></h2>${SOP_SECTIONS.map((sec, si) => {
      const items = mine.filter((r) => r.section === si);
      if (!items.length) return '';
      return `<h3>${escHtml(sec)} <span class="gu">${escHtml(SOP_SECTIONS_GU[si])}</span></h3>${items.map(({ row }) => `
        <div class="sop-pt${row.hold ? ' hold' : ''}">
          <div class="no">${escHtml(row.num)}</div>
          <div class="tx"><div class="en">${en(row.text)}</div><div class="gu">${gu(row.gu?.text || '')}</div>
          ${row.hold ? `<div class="tag">HOLD POINT · <span class="gu">${SOP_LABELS_GU.hold}</span></div>` : ''}</div>
        </div>`).join('')}`;
    }).join('')}</div>`;
  };

  const detailKeys = keys.filter((k) => ['agency_firm', 'contact_person', 'contact_phone', 'work_order_no', 'start_date', 'completion_period'].includes(k));
  const termKeys = keys.filter((k) => !detailKeys.includes(k));
  const termRow = (k: string) => {
    const p = placeholderByKey(k)!;
    return `<tr><th>${escHtml(p.label)}<div class="gu">${escHtml(p.labelGu)}</div></th><td>${value(k, 'en')}${p.translate && input.values.gu[k] ? `<div class="gu">${value(k, 'gu')}</div>` : ''}</td></tr>`;
  };
  const rates = input.rates.filter((r) => r.item.trim() || r.rate.trim());
  const rateRows = rates.length
    ? rates.map((r, i) => `<tr><td>${i + 1}</td><td>${escHtml(r.item)}${r.itemGu ? `<div class="gu">${escHtml(r.itemGu)}</div>` : ''}</td><td>${escHtml(r.unit) || BLANK_VALUE}</td><td class="num">${escHtml(r.rate) || BLANK_VALUE}</td></tr>`).join('')
    : Array.from({ length: 6 }, (_, i) => `<tr><td>${i + 1}</td><td>${BLANK_VALUE}</td><td>${BLANK_VALUE}</td><td class="num">${BLANK_VALUE}</td></tr>`).join('');

  return `<style>
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Gujarati:wght@400;600&display=swap');
  .sop-doc { font-size: 10.5pt; line-height: 1.45; color: #1c1a17; }
  .sop-doc .gu { font-family: 'Noto Sans Gujarati', 'Shruti', sans-serif; }
  .sop-doc h1 { font-family: Georgia, serif; font-weight: 400; font-size: 21pt; margin: 0; }
  .sop-doc h1 .gu { display: block; font-size: 14pt; color: #6b665e; margin-top: 2pt; }
  .sop-doc .kicker { color: #7a5525; font-size: 9.5pt; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
  .sop-doc table { width: 100%; border-collapse: collapse; margin-top: 8pt; }
  .sop-doc th, .sop-doc td { border: 1px solid #e2ddd4; padding: 4pt 6pt; text-align: left; vertical-align: top; font-size: 9.5pt; }
  .sop-doc th { width: 34%; background: #f7f3ec; font-weight: 600; }
  .sop-doc th .gu { font-weight: 400; color: #6b665e; }
  .sop-doc td .gu { color: #3d3a35; }
  .sop-doc .rates th { width: auto; }
  .sop-doc .num { text-align: right; white-space: nowrap; }
  .sop-doc .rule { font-size: 9pt; color: #6b665e; margin-top: 8pt; }
  .sop-doc h2 { font-size: 13pt; margin: 16pt 0 4pt; border-bottom: 1px solid #e2ddd4; padding-bottom: 3pt; }
  .sop-doc h2 .gu, .sop-doc h3 .gu { font-weight: 600; color: #6b665e; margin-left: 6pt; }
  .sop-doc h3 { font-size: 10.5pt; margin: 10pt 0 2pt; color: #7a5525; }
  .sop-doc .sop-pt { display: flex; gap: 8pt; padding: 5pt 4pt; border-bottom: 1px solid #efece6; page-break-inside: avoid; }
  .sop-doc .sop-pt.hold { background: #fbeae4; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sop-doc .no { width: 28pt; flex: none; font-weight: 700; color: #938b7d; font-size: 9pt; padding-top: 1pt; }
  .sop-doc .sop-pt.hold .no { color: #b3432b; }
  .sop-doc .tx { flex: 1; }
  .sop-doc .tx > .gu { color: #3d3a35; margin-top: 1pt; }
  .sop-doc .tag { display: inline-block; margin-top: 3pt; font-size: 7.5pt; font-weight: 700; color: #b3432b; border: 1px solid #e6b3a6; border-radius: 3pt; padding: 0 4pt; }
  .sop-doc .page { page-break-before: always; }
  .sop-doc .sign { display: grid; grid-template-columns: 1fr 1fr; gap: 22pt 28pt; margin-top: 26pt; }
  .sop-doc .line { border-bottom: 1px solid #1c1a17; height: 26pt; }
  .sop-doc .lbl { font-size: 9pt; color: #6b665e; margin-top: 3pt; }
</style>
<div class="sop-doc">
  <div class="kicker">Standard Operating Procedure · SOP v${input.agency.version}${input.common && !isCommon ? ` (Common v${input.common.version})` : ''}</div>
  <h1>${escHtml(nameEn)}${nameGu ? `<span class="gu">${escHtml(nameGu)}</span>` : ''}</h1>
  <table>
    <tr><th>Project<div class="gu">પ્રોજેક્ટ</div></th><td>${escHtml(input.projectName) || BLANK_VALUE}</td></tr>
    ${detailKeys.map(termRow).join('')}
    <tr><th>Issued<div class="gu">તારીખ</div></th><td>${escHtml(input.issuedOn)}${input.issuedBy ? `, ${escHtml(input.issuedBy)}` : ''}</td></tr>
  </table>
  ${termKeys.length ? `<h2>Key terms <span class="gu">મુખ્ય શરતો</span></h2><table>${termKeys.map(termRow).join('')}</table>` : ''}
  ${input.approved?.length ? `<h2>Approved materials <span class="gu">મંજૂર મટીરીયલ</span></h2><table>${input.approved.map((m) => `<tr><th>${escHtml(m.label)}${m.labelGu ? `<div class="gu">${escHtml(m.labelGu)}</div>` : ''}</th><td>${escHtml(m.value)}</td></tr>`).join('')}</table>` : ''}
  <div class="rule">A hold point means work stops there until the site engineer checks and signs. <span class="gu">હોલ્ડ પોઇન્ટ એટલે સાઇટ એન્જિનિયર ચેક કરીને સહી કરે ત્યાં સુધી કામ આગળ નહીં વધે.</span></div>
  ${isCommon ? '' : block('common', 'Common for all agencies', SOP_LABELS_GU.common)}
  ${block('agency', isCommon ? 'Common points' : `${nameEn} points`, isCommon ? '' : nameGu)}
  <div class="page">
    <h2>Annexure: Rates <span class="gu">પરિશિષ્ટ: રેટ</span></h2>
    <table class="rates"><tr><th>#</th><th>Item <span class="gu">આઇટમ</span></th><th>Unit <span class="gu">યુનિટ</span></th><th class="num">Rate (₹) <span class="gu">રેટ</span></th></tr>${rateRows}</table>
    <div class="rule">Rates as agreed for ${escHtml(input.projectName) || 'this project'}. GST and other taxes as per the work order. <span class="gu">રેટ વર્ક ઓર્ડર મુજબ, GST અને અન્ય ટેક્સ વર્ક ઓર્ડર મુજબ.</span></div>
    <h2 style="margin-top:22pt">Acknowledgement <span class="gu">સ્વીકૃતિ</span></h2>
    <p>We have read and understood this SOP (${escHtml(nameEn)}, v${input.agency.version}) for ${escHtml(input.projectName) || BLANK_VALUE}, including the key terms and rates above, and agree to follow every point, including all hold points.</p>
    <p class="gu">અમે ${escHtml(input.projectName) || BLANK_VALUE} માટેની આ SOP, ઉપરની મુખ્ય શરતો અને રેટ સહિત, વાંચી અને સમજી છે અને તમામ હોલ્ડ પોઇન્ટ સહિત દરેક પોઇન્ટનું પાલન કરવા સંમત છીએ.</p>
    <div class="sign">
      <div><div class="line"></div><div class="lbl">For ${value('agency_firm', 'en')}: name <span class="gu">નામ</span></div></div>
      <div><div class="line"></div><div class="lbl">Signature and stamp <span class="gu">સહી અને સિક્કો</span></div></div>
      <div><div class="line"></div><div class="lbl">Date <span class="gu">તારીખ</span></div></div>
      <div></div>
      <div><div class="line"></div><div class="lbl">For Swarna Group: name and signature</div></div>
      <div><div class="line"></div><div class="lbl">Date</div></div>
    </div>
  </div>
</div>`;
}

// ---------- Starter SOPs (loaded once by an admin; then edited in the app) ----------

type Seed = [number, string, boolean?, string?, GuState?];
const seedPoints = (rows: Seed[]): SopPoint[] => rows.map(([section, text, hold, gu, state], i) => ({
  id: `s${i}${Math.random().toString(36).slice(2, 8)}`, section, text, hold: !!hold, ...(gu ? { gu: { text: gu, state: state || 'ok' } } : {}),
}));

export function starterSops(): Array<Omit<SopAgency, 'log'>> {
  const mk = (id: string, name: string, nameGu: string, order: number, rows: Seed[]) => ({ id, name, nameGu, order, archived: false, projects: 'all' as const, version: 1, clauses: seedPoints(rows) });
  return [
    mk(COMMON_SOP_ID, 'Common for all agencies', SOP_LABELS_GU.common, 0, [
      [0, 'Work order issued and signed before the agency starts any work.', false, 'કોઈપણ કામ શરૂ કરતા પહેલા વર્ક ઓર્ડર ઇશ્યૂ અને સહી થયેલો હોવો જોઈએ.'],
      [0, 'Labour list with ID proof, BOCW registration and insurance submitted before the first day.', false, 'પહેલા દિવસ પહેલા લેબર લિસ્ટ, ID પ્રૂફ, BOCW રજીસ્ટ્રેશન અને ઇન્સ્યોરન્સ જમા કરાવવાના.'],
      [1, 'Helmet and safety shoes for every worker. Harness for any work above 2 m.', false, 'દરેક કામદાર માટે હેલ્મેટ અને સેફ્ટી શૂઝ. 2 મીટરથી ઉપરના કામ માટે હાર્નેસ ફરજિયાત.'],
      [1, 'Work area cleaned daily. Debris taken to the marked dumping spot.', false, 'કામની જગ્યા રોજ સાફ કરવી. કાટમાળ નક્કી કરેલી જગ્યાએ જ નાખવો.'],
      [1, 'No child labour on site.', false, 'સાઇટ પર બાળ મજૂરી નહીં.'],
      [2, 'Before an area goes to the next agency, the site engineer checks it and signs the area handover.', true, 'એરિયા બીજી એજન્સીને આપતા પહેલા સાઇટ એન્જિનિયર ચેક કરીને એરિયા હેન્ડઓવર પર સહી કરશે.'],
      [1, 'Work completed within {{completion_period}} from {{start_date}}. Delay penalty: {{delay_penalty}}.', false, '{{start_date}} થી {{completion_period}} માં કામ પૂરું કરવાનું રહેશે. વિલંબ દંડ: {{delay_penalty}}.'],
      [3, 'Extra work only against a written, approved change order.', false, 'વધારાનું કામ ફક્ત લેખિત અને મંજૂર ચેન્જ ઓર્ડર પર જ.'],
      [3, 'Running bill {{bill_cycle}} with a measurement sheet signed by the site engineer. Payment within {{payment_days}} days of certification.', false, 'સાઇટ એન્જિનિયરની સહીવાળી માપ શીટ સાથે {{bill_cycle}} રનિંગ બિલ. સર્ટિફિકેશન પછી {{payment_days}} દિવસમાં પેમેન્ટ.'],
      [3, '{{retention_percent}}% retention held from every bill and released after {{retention_period}}.', false, 'દરેક બિલમાંથી {{retention_percent}}% રીટેન્શન રાખવામાં આવશે અને {{retention_period}} પછી છૂટું કરવામાં આવશે.'],
    ]),
    mk('civil', 'Civil', 'સિવિલ', 1, [
      [0, 'Shuttering line, level and plumb checked. Cover blocks in place.'],
      [0, 'Steel cut and bent as per the approved bar bending schedule.'],
      [1, 'Block work not more than 1.5 m height per day.'],
      [1, 'Curing for at least 7 days (OPC) or 10 days (PPC).'],
      [2, 'Reinforcement checked and signed by the engineer before every pour.', true],
      [2, 'Cube samples taken for every pour. 7 and 28 day results recorded.'],
      [3, 'Measured as per IS 1200. Openings up to 0.1 m² not deducted in masonry.'],
    ]),
    mk('plumber', 'Plumber', 'પ્લમ્બર', 2, [
      [0, 'Plumbing layout drawing approved and signed by the site engineer before any wall chasing starts.', false, 'વોલ ચેઝિંગ શરૂ કરતા પહેલા પ્લમ્બિંગ લેઆઉટ ડ્રોઇંગ સાઇટ એન્જિનિયરથી મંજૂર અને સહી થયેલું હોવું જોઈએ.'],
      [0, 'Pipes and fittings checked against the approved brand and class (CPVC / UPVC) before use.', false, 'વાપરતા પહેલા પાઇપ અને ફિટિંગ મંજૂર બ્રાન્ડ અને ક્લાસ (CPVC / UPVC) મુજબ ચેક કરવા.'],
      [0, 'Sleeves for slab and beam crossings fixed before the concrete pour.', false, 'સ્લેબ અને બીમ ક્રોસિંગ માટેની સ્લીવ કોંક્રિટ ભરતા પહેલા ફિટ કરવી.'],
      [1, 'No chasing in columns or beams. Wall chase depth not more than one third of wall thickness.', false, 'કોલમ કે બીમમાં ચેઝિંગ નહીં. દીવાલમાં ચેઝની ઊંડાઈ દીવાલની જાડાઈના ત્રીજા ભાગથી વધુ નહીં.'],
      [1, 'Open pipe ends capped at the end of every working day.', false, 'દરરોજ કામ પૂરું થાય ત્યારે ખુલ્લા પાઇપના છેડા કેપ કરવા.'],
      [2, 'Pressure test of concealed lines at 1.5× working pressure for 30 minutes. Engineer signs the test sheet before plaster or tiling.', true, 'છુપાયેલી લાઈનોનો 1.5 ગણા વર્કિંગ પ્રેશરે 30 મિનિટ પ્રેશર ટેસ્ટ. પ્લાસ્ટર કે ટાઇલ્સ પહેલા એન્જિનિયર ટેસ્ટ શીટ પર સહી કરશે.'],
      [2, 'Sunken toilet area ponding test for 24 hours before filling.', true, 'સંકન ટોઇલેટ એરિયામાં ભરતી પહેલા 24 કલાક પોન્ડિંગ ટેસ્ટ.'],
      [2, 'Drainage line slope checked, minimum 1:40.', false, 'ડ્રેનેજ લાઈનનો ઢાળ ચેક કરવો, ઓછામાં ઓછો 1:40.'],
      [3, 'Billed per point as per work order. Extra points only against an approved change order.'],
      [3, 'Any leakage within {{warranty_period}} repaired free, including tile and plaster damage.'],
    ]),
    mk('electrician', 'Electrician', 'ઇલેક્ટ્રિશિયન', 3, [
      [0, "Point positions marked on the wall from the approved layout, including the flat's approved change orders."],
      [0, 'Conduits (heavy gauge, ISI) and boxes checked against approved brand.'],
      [1, 'Separate conduits for power, lighting and data/TV.'],
      [1, 'Wire sizes: 1.5 sq mm lighting, 2.5 sq mm power, 4 sq mm AC and geyser. Colour code followed.'],
      [2, 'Slab conduits and junction boxes checked by the engineer before the pour.', true],
      [2, 'Insulation (megger) test done and recorded for every flat before handover.', true],
      [3, 'Fixtures fitted only after the final coat of paint.'],
    ]),
    mk('flooring', 'Stone & Flooring', 'સ્ટોન અને ફ્લોરિંગ', 4, [
      [0, 'Plumbing and electrical tests in the area signed off before tiling starts.', true],
      [0, 'Tile and granite batch and shade checked. One batch per flat.'],
      [1, 'Approved tile adhesive and spacers used. Joint width as per drawing.'],
      [2, 'Tap test on every room. No hollow-sounding tiles accepted.'],
      [2, 'Bathroom and balcony floor slope to the floor trap checked with water.'],
      [3, 'Finished floor covered and protected until handover.'],
    ]),
    mk('carpenter', 'Carpenter', 'કારપેન્ટર', 5, [
      [0, 'Door frame position, level and plumb checked before plaster.'],
      [0, 'BWP grade ply used in wet areas.'],
      [2, 'Doors close smoothly with even gaps. Hardware as per approved brand.'],
      [3, 'Billed per door or running foot as per work order.'],
    ]),
    mk('aluminium', 'Aluminium', 'એલ્યુમિનિયમ', 6, [
      [0, 'Opening sizes measured at site after plaster, not taken from the drawing.'],
      [0, 'Section thickness and brand checked against the approved sample.'],
      [1, 'Silicone sealant applied inside and outside. Drain holes in the bottom track.'],
      [2, 'Hose water test on every window. No leakage before handover.', true],
      [3, "Billed per sq ft on the frame's outer size."],
    ]),
    mk('painter', 'Painter', 'પેઇન્ટર', 7, [
      [0, 'Plaster cured at least 28 days and dry before putty.'],
      [0, 'Shade approved on a sample patch at site.'],
      [1, 'System: 2 coats putty, 1 coat primer, 2 finish coats.'],
      [1, 'Switches, fittings, windows and floors masked before painting.'],
      [2, 'Walls checked under side light before the final coat.'],
      [3, 'Touch-up after electrical and carpentry fitting is included in the rate.'],
    ]),
  ];
}
